import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaLock, FaHeart, FaUsers, FaClipboardList, FaUserEdit, FaSearch, FaComments, FaQuoteLeft, FaStar, FaCheck, FaArrowRight, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
// GiOilLamp replaced with emoji to avoid slow gi import

/* ------------------------------------------------------------------ */
/*  Animated counter hook                                              */
/* ------------------------------------------------------------------ */
const useCountUp = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return { count, ref };
};

/* ------------------------------------------------------------------ */
/*  Pricing Card                                                       */
/* ------------------------------------------------------------------ */
const PricingCard = ({ name, price, features, popular }) => (
  <div
    className={`relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 ${
      popular
        ? 'bg-gradient-to-br from-[#8B0000] to-[#DC143C] text-white shadow-2xl scale-105'
        : 'bg-white border-2 border-[#FFD700] text-[#333] shadow-lg'
    }`}
  >
    {popular && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FFD700] text-[#8B0000] text-sm font-bold px-5 py-1 rounded-full shadow">
        लोकप्रिय
      </div>
    )}
    <h3 className={`text-xl font-['Playfair_Display'] font-bold mb-1 ${popular ? 'text-[#FFD700]' : 'text-[#8B0000]'}`}>
      {name}
    </h3>
    <div className="mb-6">
      <span className="text-4xl font-bold">₹{price}</span>
      <span className={`text-sm ${popular ? 'text-white/70' : 'text-gray-500'}`}> / 3 महिने</span>
    </div>
    <ul className="space-y-3 mb-8">
      {features.map((f, i) => (
        <li key={i} className="flex items-center gap-2 text-sm">
          <FaCheck className={popular ? 'text-[#FFD700]' : 'text-green-500'} />
          {f}
        </li>
      ))}
    </ul>
    <Link
      to="/register"
      className={`block text-center py-3 rounded-lg font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
        popular
          ? 'bg-[#FFD700] text-[#8B0000] hover:shadow-lg'
          : 'bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white hover:shadow-lg'
      }`}
    >
      निवडा
    </Link>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Floating Diya                                                      */
/* ------------------------------------------------------------------ */
const FloatingDiya = ({ style }) => (
  <div className="absolute text-[#FFD700] opacity-20 pointer-events-none" style={style}>
    <span style={{fontSize: '28px'}}>🪔</span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Footer Component                                                   */
/* ------------------------------------------------------------------ */
const Footer = () => (
  <footer className="bg-[#8B0000] text-white">
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <h3 className="text-2xl font-['Noto_Sans_Devanagari'] font-bold text-[#FFD700] mb-4">विवाह आरंभ</h3>
          <p className="text-white/70 text-sm leading-relaxed">
            Premium Hindu Vanjari Matrimonial Platform. Connecting hearts with trust and tradition.
          </p>
        </div>
        <div>
          <h4 className="font-['Playfair_Display'] font-bold text-[#FFD700] mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/about" className="hover:text-[#FFD700] transition-colors">About Us</Link></li>
            <li><Link to="/pricing" className="hover:text-[#FFD700] transition-colors">Pricing</Link></li>
            <li><Link to="/register" className="hover:text-[#FFD700] transition-colors">Register</Link></li>
            <li><Link to="/login" className="hover:text-[#FFD700] transition-colors">Login</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-['Playfair_Display'] font-bold text-[#FFD700] mb-4">Legal</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><a href="#" className="hover:text-[#FFD700] transition-colors">Privacy Policy</a></li>
            <li><a href="#" className="hover:text-[#FFD700] transition-colors">Terms of Service</a></li>
            <li><a href="#" className="hover:text-[#FFD700] transition-colors">Refund Policy</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-['Playfair_Display'] font-bold text-[#FFD700] mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="flex items-center gap-2"><FaPhoneAlt className="text-[#FFD700]" /> +91 98765 43210</li>
            <li className="flex items-center gap-2"><FaEnvelope className="text-[#FFD700]" /> info@vivaharambh.com</li>
            <li className="flex items-center gap-2"><FaMapMarkerAlt className="text-[#FFD700]" /> Pune, Maharashtra</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/20 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center text-sm text-white/50">
        <p>&copy; 2026 Vivah Arambh. All rights reserved.</p>
        <p>Developed by <span className="text-[#FFD700]">Lyftek Solutions</span></p>
      </div>
    </div>
  </footer>
);

/* ================================================================== */
/*  LANDING PAGE                                                       */
/* ================================================================== */
const LandingPage = () => {
  const stat1 = useCountUp(10000);
  const stat2 = useCountUp(5000);
  const stat3 = useCountUp(50);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      quote: 'विवाह आरंभ मुळे आम्हाला योग्य जोडीदार मिळाला. खूप छान अनुभव होता!',
      name: 'प्रिया पाटील',
      city: 'पुणे',
      rating: 5,
    },
    {
      quote: 'या प्लॅटफॉर्मवर सत्यापित प्रोफाइल असल्यामुळे विश्वास ठेवता आला. धन्यवाद!',
      name: 'राहुल जाधव',
      city: 'मुंबई',
      rating: 5,
    },
    {
      quote: 'आमच्या समुदायासाठी खास बनवलेले हे प्लॅटफॉर्म खरोखरच उत्तम आहे.',
      name: 'सुनीता शिंदे',
      city: 'नाशिक',
      rating: 5,
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((p) => (p + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen bg-[#FFF8F0]">
      {/* ============================================================ */}
      {/*  NAVIGATION BAR                                               */}
      {/* ============================================================ */}
      <nav className="absolute top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-3xl font-['Noto_Sans_Devanagari'] font-bold text-[#FFD700] drop-shadow-lg">
              विवाह आरंभ
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/about" className="text-white/90 hover:text-[#FFD700] transition-colors font-medium">About</Link>
            <Link to="/pricing" className="text-white/90 hover:text-[#FFD700] transition-colors font-medium">Pricing</Link>
            <Link to="/login" className="text-white/90 hover:text-[#FFD700] transition-colors font-medium">Login</Link>
            <Link
              to="/register"
              className="bg-[#FFD700] text-[#8B0000] px-5 py-2 rounded-full font-bold hover:bg-[#FFF8DC] transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
            >
              नोंदणी करा
            </Link>
          </div>
        </div>
      </nav>

      {/* ============================================================ */}
      {/*  HERO SECTION                                                 */}
      {/* ============================================================ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B0000] via-[#DC143C] to-[#8B0000]" />

        {/* Gold shimmer overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(45deg, transparent 30%, rgba(255,215,0,0.4) 50%, transparent 70%)',
            backgroundSize: '300% 300%',
            animation: 'shimmer 4s ease-in-out infinite',
          }}
        />

        {/* Mandala pattern overlay */}
        <div className="absolute inset-0 opacity-[0.06]">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              width: '900px',
              height: '900px',
              borderRadius: '50%',
              background:
                'repeating-radial-gradient(circle at center, transparent 0px, transparent 40px, rgba(255,215,0,0.3) 41px, transparent 42px)',
              animation: 'mandalaRotate 60s linear infinite',
            }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              width: '700px',
              height: '700px',
              borderRadius: '50%',
              background:
                'repeating-radial-gradient(circle at center, transparent 0px, transparent 30px, rgba(255,255,255,0.2) 31px, transparent 32px)',
              animation: 'mandalaRotate 45s linear infinite reverse',
            }}
          />
        </div>

        {/* Floating diyas */}
        <FloatingDiya style={{ top: '15%', left: '8%', animation: 'float 3s ease-in-out infinite' }} />
        <FloatingDiya style={{ top: '25%', right: '12%', animation: 'float 4s ease-in-out 1s infinite' }} />
        <FloatingDiya style={{ bottom: '20%', left: '15%', animation: 'float 3.5s ease-in-out 0.5s infinite' }} />
        <FloatingDiya style={{ bottom: '30%', right: '8%', animation: 'float 4.5s ease-in-out 1.5s infinite' }} />
        <FloatingDiya style={{ top: '60%', left: '5%', animation: 'float 3s ease-in-out 2s infinite' }} />
        <FloatingDiya style={{ top: '40%', right: '5%', animation: 'float 3.8s ease-in-out 0.8s infinite' }} />

        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-[#FFD700] opacity-20"
            style={{
              width: `${4 + Math.random() * 6}px`,
              height: `${4 + Math.random() * 6}px`,
              top: `${10 + Math.random() * 80}%`,
              left: `${5 + Math.random() * 90}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out ${Math.random() * 3}s infinite`,
            }}
          />
        ))}

        {/* Hero content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {/* Om decoration */}
          <div className="text-[#FFD700] text-3xl mb-4 opacity-60 font-['Noto_Sans_Devanagari'] animate-fade-in">
            ॐ
          </div>

          <h1 className="text-6xl md:text-8xl font-['Noto_Sans_Devanagari'] font-bold text-[#FFD700] mb-4 animate-slide-up drop-shadow-2xl">
            विवाह आरंभ
          </h1>

          <div className="divider-ornament mb-4">
            <span className="text-[#FFD700]">✦</span>
          </div>

          <p className="text-xl md:text-2xl font-['Noto_Sans_Devanagari'] text-white/90 mb-3 animate-slide-up delay-200">
            शुभ विवाहाची सुरुवात इथून होते
          </p>

          <p className="text-lg font-['Playfair_Display'] text-[#FFD700]/80 mb-10 tracking-wider animate-slide-up delay-300">
            Premium Hindu Vanjari Matrimonial Platform
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up delay-400">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-[#FFD700] text-[#8B0000] px-10 py-4 rounded-full text-lg font-bold hover:bg-[#FFF8DC] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              नोंदणी करा <FaArrowRight />
            </Link>
            <Link
              to="/browse"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#FFD700] text-[#FFD700] px-10 py-4 rounded-full text-lg font-bold hover:bg-[#FFD700] hover:text-[#8B0000] transition-all duration-300 hover:-translate-y-1"
            >
              प्रोफाइल शोधा <FaSearch />
            </Link>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 80L48 72C96 64 192 48 288 42.7C384 37 480 43 576 53.3C672 64 768 80 864 80C960 80 1056 64 1152 53.3C1248 43 1344 37 1392 34.7L1440 32V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V80Z"
              fill="#FFF8F0"
            />
          </svg>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURES SECTION                                             */}
      {/* ============================================================ */}
      <section className="py-20 px-6 bg-[#FFF8F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#8B0000] mb-3">
              आमची वैशिष्ट्ये
            </h2>
            <p className="text-gray-600 font-['Playfair_Display']">Why choose Vivah Arambh?</p>
            <div className="divider-ornament mt-4">
              <span className="text-[#B8860B]">✦</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaShieldAlt size={32} />,
                title: 'सत्यापित प्रोफाइल',
                desc: 'Every profile is verified with Aadhaar and personal checks for your safety.',
              },
              {
                icon: <FaLock size={32} />,
                title: 'गोपनीयता सुरक्षा',
                desc: 'Your personal information stays protected with advanced privacy controls.',
              },
              {
                icon: <FaHeart size={32} />,
                title: 'स्मार्ट जुळणी',
                desc: 'AI-powered matching algorithm finds the most compatible partners for you.',
              },
              {
                icon: <FaUsers size={32} />,
                title: 'समुदाय केंद्रित',
                desc: 'Built exclusively for the Hindu Vanjari community with cultural values.',
              },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 border-2 border-[#FFD700]/30 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 text-center group"
              >
                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#DC143C] to-[#8B0000] text-white flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  {f.icon}
                </div>
                <h3 className="text-xl font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-3">
                  {f.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  HOW IT WORKS                                                 */}
      {/* ============================================================ */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#FFF8F0] to-[#FFE4C4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#8B0000] mb-3">
              कसे काम करते?
            </h2>
            <p className="text-gray-600 font-['Playfair_Display']">Simple steps to find your life partner</p>
            <div className="divider-ornament mt-4">
              <span className="text-[#B8860B]">✦</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting dotted line (desktop) */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] border-t-2 border-dashed border-[#B8860B]/40 z-0" />

            {[
              { icon: <FaClipboardList size={24} />, step: '१', title: 'नोंदणी करा', desc: 'Register with basic details' },
              { icon: <FaUserEdit size={24} />, step: '२', title: 'प्रोफाइल तयार करा', desc: 'Complete your profile' },
              { icon: <FaSearch size={24} />, step: '३', title: 'जोडीदार शोधा', desc: 'Search & find matches' },
              { icon: <FaComments size={24} />, step: '४', title: 'संपर्क करा', desc: 'Connect & communicate' },
            ].map((s, i) => (
              <div key={i} className="text-center relative z-10">
                <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-white border-3 border-[#FFD700] shadow-lg flex items-center justify-center relative">
                  <div className="text-[#DC143C]">{s.icon}</div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-[#DC143C] to-[#8B0000] text-white text-sm font-bold flex items-center justify-center font-['Noto_Sans_Devanagari']">
                    {s.step}
                  </div>
                </div>
                <h3 className="text-lg font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000] mb-2">
                  {s.title}
                </h3>
                <p className="text-gray-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  STATS SECTION                                                */}
      {/* ============================================================ */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#8B0000] via-[#DC143C] to-[#8B0000] relative overflow-hidden">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundImage:
                'radial-gradient(circle, rgba(255,215,0,0.3) 1px, transparent 1px)',
              backgroundSize: '30px 30px',
            }}
          />
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 text-center relative z-10">
          {[
            { ref: stat1.ref, count: stat1.count, suffix: '+', label: 'प्रोफाइल', sublabel: 'Profiles' },
            { ref: stat2.ref, count: stat2.count, suffix: '+', label: 'यशस्वी जुळण्या', sublabel: 'Successful Matches' },
            { ref: stat3.ref, count: stat3.count, suffix: '+', label: 'शहरे', sublabel: 'Cities' },
          ].map((s, i) => (
            <div key={i} ref={s.ref}>
              <div className="text-5xl md:text-6xl font-bold text-[#FFD700] mb-2 font-['Playfair_Display']">
                {s.count.toLocaleString()}{s.suffix}
              </div>
              <div className="text-xl font-['Noto_Sans_Devanagari'] text-white font-semibold">{s.label}</div>
              <div className="text-white/60 text-sm">{s.sublabel}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/*  TESTIMONIALS                                                 */}
      {/* ============================================================ */}
      <section className="py-20 px-6 bg-[#FFF8F0]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#8B0000] mb-3">
              यशोगाथा
            </h2>
            <p className="text-gray-600 font-['Playfair_Display']">Success Stories from our members</p>
            <div className="divider-ornament mt-4">
              <span className="text-[#B8860B]">✦</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-8 shadow-md border border-[#FFD700]/20 transition-all duration-500 ${
                  i === activeTestimonial ? 'scale-105 shadow-xl border-[#FFD700]' : 'scale-100'
                }`}
              >
                <FaQuoteLeft className="text-[#DC143C]/20 text-3xl mb-4" />
                <p className="font-['Noto_Sans_Devanagari'] text-gray-700 leading-relaxed mb-6 text-lg">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(t.rating)].map((_, j) => (
                    <FaStar key={j} className="text-[#FFD700] text-sm" />
                  ))}
                </div>
                <div className="border-t border-[#FFD700]/30 pt-4">
                  <p className="font-['Noto_Sans_Devanagari'] font-bold text-[#8B0000]">{t.name}</p>
                  <p className="text-gray-500 text-sm font-['Noto_Sans_Devanagari']">{t.city}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Carousel dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveTestimonial(i)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  i === activeTestimonial ? 'bg-[#DC143C] w-8' : 'bg-[#DC143C]/30'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  PRICING PREVIEW                                              */}
      {/* ============================================================ */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#FFF8F0] to-[#FFE4C4]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#8B0000] mb-3">
              योजना निवडा
            </h2>
            <p className="text-gray-600 font-['Playfair_Display']">Choose a plan that works for you</p>
            <div className="divider-ornament mt-4">
              <span className="text-[#B8860B]">✦</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <PricingCard
              name="Basic"
              price="1,000"
              features={[
                'प्रोफाइल तयार करा',
                '10 संपर्क माहिती',
                'बेसिक जुळणी',
                'ईमेल सपोर्ट',
              ]}
            />
            <PricingCard
              name="Premium"
              price="1,500"
              popular
              features={[
                'अमर्याद संपर्क माहिती',
                'स्मार्ट जुळणी',
                'प्रोफाइल हायलाइट',
                'चॅट सुविधा',
                'फोन सपोर्ट',
              ]}
            />
            <PricingCard
              name="Royal"
              price="2,000"
              features={[
                'Premium सर्व सुविधा',
                'वैयक्तिक सल्लागार',
                'प्रायोरिटी लिस्टिंग',
                'प्रोफाइल बूस्ट',
                'व्हिडिओ कॉल',
              ]}
            />
          </div>

          <div className="text-center mt-10">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-[#DC143C] font-semibold hover:text-[#8B0000] transition-colors"
            >
              View All Plans <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FINAL CTA                                                    */}
      {/* ============================================================ */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#8B0000] via-[#DC143C] to-[#8B0000] relative overflow-hidden">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="w-full h-full"
            style={{
              backgroundImage:
                'radial-gradient(circle, rgba(255,215,0,0.5) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div className="text-[#FFD700] text-2xl mb-4 font-['Noto_Sans_Devanagari'] opacity-60">✦ ✦ ✦</div>
          <h2 className="text-4xl md:text-5xl font-['Noto_Sans_Devanagari'] font-bold text-[#FFD700] mb-6">
            आजच नोंदणी करा!
          </h2>
          <p className="text-white/80 text-lg mb-10 font-['Noto_Sans_Devanagari']">
            तुमच्या जीवनसाथीची शोध इथून सुरू करा. हजारो सत्यापित प्रोफाइल तुमची वाट पाहत आहेत.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-3 bg-[#FFD700] text-[#8B0000] px-12 py-4 rounded-full text-xl font-bold hover:bg-[#FFF8DC] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
          >
            नोंदणी करा <FaArrowRight />
          </Link>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FOOTER                                                       */}
      {/* ============================================================ */}
      <Footer />
    </div>
  );
};

export default LandingPage;
